import React, { useMemo, useState, useEffect } from 'react'
import { ResponsiveBar } from '@nivo/bar'
import { Card } from 'react-bootstrap'
import { formatDateOnly, parseDateOnly } from '../utils/commonUtils'

const InventoryTimelineGraph = ({ data }) => {
    const [isDarkMode, setIsDarkMode] = useState(false)

    useEffect(() => {
        // Check if dark mode is active
        const checkDarkMode = () => {
            const htmlElement = document.documentElement
            const theme = htmlElement.getAttribute('data-bs-theme')
            setIsDarkMode(theme === 'dark')
        }

        checkDarkMode()
        
        // Listen for theme changes
        const observer = new MutationObserver(checkDarkMode)
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-bs-theme'] })
        
        return () => {
            observer.disconnect()
        }
    }, [])
    // Create a map to store shipment dates for sorting and color generation
    const shipmentDates = useMemo(() => {
        const dates = new Map()
        const confirmedShipments = data?.confirmed_shipments || []
        
        // Filter and sort confirmed shipments with known dates
        const shipmentsWithDates = confirmedShipments.filter(s => s.expected_date)
        const sortedConfirmed = [...shipmentsWithDates].sort((a, b) => 
            new Date(a.expected_date) - new Date(b.expected_date)
        )
        
        // Add confirmed shipments dates with their index for color generation
        sortedConfirmed.forEach((shipment, index) => {
            const key = `confirmed_${shipment.po_number}_${shipment.cases}`
            dates.set(key, {
                date: new Date(shipment.expected_date),
                colorIndex: index,
                totalConfirmed: sortedConfirmed.length,
                dateStatus: shipment.date_status
            })
        })
        
        return dates
    }, [data])

    // Helper function to generate green shades
    const getGreenShade = (index, total) => {
        // Base green color: rgb(76, 175, 80)
        const baseHue = 122 // Green hue
        const baseSaturation = 57 // Base saturation percentage
        const baseLightness = 49 // Base lightness percentage
        
        // If only one shipment, return base color
        if (total === 1) {
            return `hsl(${baseHue}, ${baseSaturation}%, ${baseLightness}%)`
        }
        
        // Adjust lightness based on position
        // Earlier shipments will be darker, later ones lighter
        const lightnessAdjustment = (index / (total - 1)) * 30 - 15 // Range: -15 to +15
        const finalLightness = Math.max(20, Math.min(80, baseLightness + lightnessAdjustment))
        
        return `hsl(${baseHue}, ${baseSaturation}%, ${finalLightness}%)`
    }

    // Get current quantity in cases for all UOM types
    // For pallet/film items, we need to get the raw quantity (cases before division)
    const { currentCasesQuantity, totalPallets, totalFilm, totalClearFilm } = useMemo(() => {
        if (!data?.current_inventory?.total) {
            return { currentCasesQuantity: 0, totalPallets: 0, totalFilm: 0, totalClearFilm: 0 }
        }

        const totals = data.current_inventory.total

        // For pallet/film items, we need to calculate cases from the pallet/film total
        // by multiplying back by case_pack, but we don't have case_pack here
        // Instead, we'll use the sum of quantities from by_location data
        let casesQuantity = totals.total_cases

        if (totals.total_cases === 0 && data.current_inventory?.by_location) {
            // Sum up raw quantities from locations (this gives us cases for pallet/film items)
            casesQuantity = data.current_inventory.by_location.reduce((sum, loc) => {
                return sum + (loc.quantity || loc.cases || 0)
            }, 0)
        }

        return {
            currentCasesQuantity: casesQuantity,
            totalPallets: totals.total_pallets || 0,
            totalFilm: totals.total_film || 0,
            totalClearFilm: totals.total_clear_film || 0
        }
    }, [data])

    const chartData = useMemo(() => {
        if (!data?.current_inventory?.total) return []

        const now = new Date()
        const result = []

        const currentCases = currentCasesQuantity // Use cases for graph display

        // Build info string with both cases and pallets/impressions if applicable
        const buildInventoryInfo = () => {
            let info = `${currentCases} cases`
            if (totalPallets > 0) {
                info += ` (${totalPallets} pallets)`
            }
            if (totalFilm > 0) {
                info += ` (${totalFilm} impressions)`
            }
            if (totalClearFilm > 0) {
                info += ` (${totalClearFilm} rolls)`
            }
            return info
        }

        const inventoryInfo = `Current Inventory: ${buildInventoryInfo()}`
        // Use local date instead of UTC to avoid timezone issues
        const year = now.getFullYear()
        const month = String(now.getMonth() + 1).padStart(2, '0')
        const day = String(now.getDate()).padStart(2, '0')
        const todayDate = `${year}-${month}-${day}`

        // Separate confirmed shipments into those with dates and those without
        const shipmentsWithDates = []
        const shipmentsWithoutDates = []
        
        if (data.confirmed_shipments) {
            data.confirmed_shipments.forEach(shipment => {
                if (shipment.expected_date) {
                    shipmentsWithDates.push(shipment)
                } else {
                    shipmentsWithoutDates.push(shipment)
                }
            })
        }

        // Sort shipments with dates by expected_date
        shipmentsWithDates.sort((a, b) => new Date(a.expected_date) - new Date(b.expected_date))

        // Group shipments by expected_date (multiple shipments can have the same date)
        const shipmentsByDate = {}
        shipmentsWithDates.forEach(shipment => {
            if (!shipmentsByDate[shipment.expected_date]) {
                shipmentsByDate[shipment.expected_date] = []
            }
            shipmentsByDate[shipment.expected_date].push(shipment)
        })

        // Get unique dates in sorted order
        const uniqueDates = Object.keys(shipmentsByDate).sort()

        // Check if any shipment has today's date
        const hasShipmentToday = uniqueDates.includes(todayDate)

        // Add current inventory point only if no shipments are dated today
        if (!hasShipmentToday) {
            const currentPoint = {
                date: todayDate,
                label: 'Current',
                currentCases,
                segments: [{
                    type: 'current',
                    cases: currentCases,
                    info: inventoryInfo
                }]
            }
            currentPoint[`current_${currentCases}`] = currentCases
            result.push(currentPoint)
        }

        // Helper to parse date strings as local dates
        const parseLocalDate = (dateString) => {
            if (!dateString) return null
            const [y, m, d] = dateString.split('-').map(Number)
            return new Date(y, m - 1, d)
        }

        // Process each unique date (may have multiple shipments per date)
        uniqueDates.forEach((dateStr, dateIndex) => {
            const shipmentsForDate = shipmentsByDate[dateStr]
            const isToday = dateStr === todayDate
            
            // Parse expected_date as local date to avoid timezone shift
            const [year, month, day] = dateStr.split('-').map(Number)
            const expectedLocalDate = new Date(year, month - 1, day)
            
            const point = {
                date: dateStr,
                label: isToday ? 'Current + Incoming' : `Expected ${expectedLocalDate.toLocaleDateString()}`,
                currentCases,
                segments: []
            }

            // Add current inventory segment
            point.segments.push({
                type: 'current',
                cases: currentCases,
                info: inventoryInfo
            })

            // Add base current inventory as first stack
            point[`current_${currentCases}`] = currentCases

            // Copy over previous confirmed segments (but skip if this is today - start fresh)
            if (!isToday) {
                const previousPoint = result[result.length - 1]
                if (previousPoint?.segments) {
                    previousPoint.segments.forEach(segment => {
                        if (segment.type === 'confirmed') {
                            point.segments.push({ ...segment })
                            point[`confirmed_${segment.poNumber}_${segment.cases}`] = segment.cases
                        }
                    })
                }
            }

            // Add ALL shipments for this date as separate stacks
            shipmentsForDate.forEach(shipment => {
                const stackKey = `confirmed_${shipment.po_number}_${shipment.cases}`
                point[stackKey] = shipment.cases
                
                point.segments.push({
                    type: 'confirmed',
                    poNumber: shipment.po_number,
                    cases: shipment.cases,
                    vendor: shipment.vendor,
                    shipDate: parseLocalDate(shipment.ship_date),
                    etd: parseLocalDate(shipment.etd),
                    eta: parseLocalDate(shipment.eta),
                    dateStatus: shipment.date_status,
                    confirmationDate: new Date(shipment.confirmation_date)
                })
            })

            result.push(point)
        })

        // Add "Date Unknown" column if there are shipments without dates
        if (shipmentsWithoutDates.length > 0) {
            const unknownPoint = {
                date: 'unknown',
                label: 'Date Unknown',
                currentCases,
                segments: [{
                    type: 'current',
                    cases: currentCases,
                    info: inventoryInfo
                }]
            }
            unknownPoint[`current_${currentCases}`] = currentCases

            // Add each unknown shipment as a stack
            shipmentsWithoutDates.forEach(shipment => {
                unknownPoint[`unknown_${shipment.po_number}_${shipment.cases}`] = shipment.cases
                unknownPoint.segments.push({
                    type: 'date_unknown',
                    poNumber: shipment.po_number,
                    cases: shipment.cases,
                    vendor: shipment.vendor,
                    confirmationDate: new Date(shipment.confirmation_date),
                    dateStatus: 'date_unknown'
                })
            })

            result.push(unknownPoint)
        }

        return result
    }, [data, currentCasesQuantity, totalPallets, totalFilm, totalClearFilm])

    // Generate keys for the stacked bar chart
    const keys = useMemo(() => {
        if (chartData.length === 0) return []
        
        const allKeys = new Set()
        chartData.forEach(point => {
            Object.keys(point).forEach(key => {
                if (key.startsWith('current_') || 
                    key.startsWith('confirmed_') || 
                    key.startsWith('unknown_')) {
                    allKeys.add(key)
                }
            })
        })
        
        // Sort keys to ensure consistent stacking order
        return Array.from(allKeys).sort((a, b) => {
            // Current inventory always at bottom
            if (a.startsWith('current_')) return -1
            if (b.startsWith('current_')) return 1

            // Unknown dates at top (last in stack)
            if (a.startsWith('unknown_') && !b.startsWith('unknown_')) return 1
            if (!a.startsWith('unknown_') && b.startsWith('unknown_')) return -1
            if (a.startsWith('unknown_') && b.startsWith('unknown_')) return 0

            // Get dates for both keys
            const dateA = shipmentDates.get(a)
            const dateB = shipmentDates.get(b)

            // If both are confirmed, sort by date
            if (dateA && dateB) {
                return dateA.date - dateB.date
            }

            return 0
        })
    }, [chartData, shipmentDates])

    const formatTooltipSegment = (segment) => {
        switch (segment.type) {
            case 'current':
                return segment.info
            case 'confirmed': {
                let confirmationInfo = `Confirmed PO #${segment.poNumber} (${segment.vendor})\n` +
                       `Cases: ${segment.cases}\n` +
                       `Confirmation Date: ${segment.confirmationDate.toLocaleDateString()}\n`
                
                if (segment.dateStatus === 'has_eta') {
                    confirmationInfo += `ETA: ${segment.eta.toLocaleDateString()}\n`
                    if (segment.etd) {
                        confirmationInfo += `ETD: ${segment.etd.toLocaleDateString()}`
                    }
                } else if (segment.dateStatus === 'has_etd_only') {
                    confirmationInfo += `⚠ Using ETD (ETA not set)\n`
                    confirmationInfo += `ETD: ${segment.etd.toLocaleDateString()}`
                }
                
                return confirmationInfo
            }
            case 'date_unknown':
                return `⚠ Date Unknown - PO #${segment.poNumber} (${segment.vendor})\n` +
                       `Cases: ${segment.cases}\n` +
                       `Confirmation Date: ${segment.confirmationDate.toLocaleDateString()}\n` +
                       `⚠ Please update ETA/ETD on this confirmation`
            default:
                return ''
        }
    }

    if (!data?.current_inventory?.total) {
        return null
    }

    // Check if there are any shipments without dates
    const shipmentsWithoutDates = data.confirmed_shipments?.filter(s => !s.expected_date) || []
    const hasDateUnknownShipments = shipmentsWithoutDates.length > 0

    return (
        <Card className="mb-4">
            <Card.Header>
                <h4 className="mb-0">Inventory Timeline Forecast</h4>
            </Card.Header>
            <Card.Body>
                {hasDateUnknownShipments && (
                    <div className="alert alert-warning mb-3" role="alert">
                        <strong>⚠ Warning:</strong> {shipmentsWithoutDates.length} confirmed shipment{shipmentsWithoutDates.length > 1 ? 's have' : ' has'} no delivery date set (no ETA or ETD). 
                        These items appear in the &quot;Date Unknown&quot; column. Please update the confirmation records with expected delivery dates.
                    </div>
                )}
                <div style={{ height: '400px', width: '100%' }}>
                    <ResponsiveBar
                        data={chartData}
                        keys={keys}
                        indexBy="date"
                        margin={{ top: 50, right: 130, bottom: 80, left: 60 }}
                        padding={0.3}
                        valueScale={{ type: 'linear' }}
                        indexScale={{ type: 'band', round: true }}
                        colors={({ id }) => {
                            if (id.startsWith('current_')) return '#2196F3' // Blue
                            if (id.startsWith('unknown_')) return '#FF5722' // Red/Orange for date unknown
                            if (id.startsWith('confirmed_')) {
                                const info = shipmentDates.get(id)
                                if (info) {
                                    // Use cyan/teal for items with ETD only (no ETA)
                                    if (info.dateStatus === 'has_etd_only') {
                                        return '#00BCD4' // Cyan/Teal
                                    }
                                    // Use green shades for items with ETA
                                    return getGreenShade(info.colorIndex, info.totalConfirmed)
                                }
                                return '#4CAF50' // Fallback green
                            }
                            return '#000000'
                        }}
                        borderColor={{
                            from: 'color',
                            modifiers: [['darker', 1.6]]
                        }}
                        axisTop={null}
                        axisRight={null}
                        axisBottom={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: -45,
                            format: value => {
                                if (value === 'unknown') return '⚠ Date Unknown'
                                // Parse date as local date (not UTC) to avoid timezone shift
                                const [year, month, day] = value.split('-').map(Number)
                                const localDate = new Date(year, month - 1, day)
                                return localDate.toLocaleDateString()
                            },
                            legend: 'Timeline',
                            legendPosition: 'middle',
                            legendOffset: 60,
                            tickColor: isDarkMode ? '#ffffff' : '#000000',
                            legendColor: isDarkMode ? '#ffffff' : '#000000',
                            renderTick: ({ x, y, value, tickIndex, format }) => (
                                <g transform={`translate(${x},${y})`}>
                                    <text
                                        x={0}
                                        y={0}
                                        dy={16}
                                        textAnchor="end"
                                        fill={value === 'unknown' ? '#FF5722' : (isDarkMode ? '#ffffff' : '#000000')}
                                        fontSize={11}
                                        fontWeight={value === 'unknown' ? 'bold' : 'normal'}
                                        transform="rotate(-45)"
                                    >
                                        {format(value)}
                                    </text>
                                </g>
                            )
                        }}
                        axisLeft={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            legend: 'Cases',
                            legendPosition: 'middle',
                            legendOffset: -40,
                            tickColor: isDarkMode ? '#ffffff' : '#000000',
                            legendColor: isDarkMode ? '#ffffff' : '#000000',
                            renderTick: ({ x, y, value, tickIndex, format }) => (
                                <g transform={`translate(${x},${y})`}>
                                    <text
                                        x={-10}
                                        y={0}
                                        textAnchor="end"
                                        fill={isDarkMode ? '#ffffff' : '#000000'}
                                        fontSize={11}
                                    >
                                        {value}
                                    </text>
                                </g>
                            )
                        }}
                        labelSkipWidth={12}
                        labelSkipHeight={12}
                        labelTextColor={isDarkMode ? '#ffffff' : '#000000'}
                        legends={[
                            {
                                dataFrom: 'keys',
                                anchor: 'bottom-right',
                                direction: 'column',
                                justify: false,
                                translateX: 120,
                                translateY: 0,
                                itemsSpacing: 2,
                                itemWidth: 100,
                                itemHeight: 20,
                                itemDirection: 'left-to-right',
                                itemOpacity: 0.85,
                                symbolSize: 20,
                                itemTextColor: isDarkMode ? '#ffffff' : '#000000',
                                effects: [
                                    {
                                        on: 'hover',
                                        style: {
                                            itemOpacity: 1
                                        }
                                    }
                                ],
                                data: [
                                    {
                                        id: 'current',
                                        label: 'Current',
                                        color: '#2196F3'
                                    },
                                    {
                                        id: 'confirmed_eta',
                                        label: 'Confirmed (ETA)',
                                        color: '#4CAF50'
                                    },
                                    {
                                        id: 'confirmed_etd',
                                        label: 'Confirmed (ETD)',
                                        color: '#00BCD4'
                                    },
                                    {
                                        id: 'date_unknown',
                                        label: '⚠ Date Unknown',
                                        color: '#FF5722'
                                    }
                                ]
                            }
                        ]}
                        tooltip={({ indexValue, data }) => {
                            const totalCases = data.segments.reduce((sum, seg) => sum + seg.cases, 0)
                            // Parse date as local date (not UTC) to avoid timezone shift
                            let dateLabel = '⚠ Date Unknown'
                            if (indexValue !== 'unknown') {
                                const [year, month, day] = indexValue.split('-').map(Number)
                                const localDate = new Date(year, month - 1, day)
                                dateLabel = localDate.toLocaleDateString()
                            }
                            
                            return (
                                <div
                                    style={{
                                        padding: 12,
                                        background: isDarkMode ? '#2d3748' : '#ffffff',
                                        border: isDarkMode ? '1px solid #4a5568' : '1px solid #ccc',
                                        borderRadius: '4px',
                                        whiteSpace: 'pre-line',
                                        minWidth: '300px',
                                        fontSize: '12px',
                                        lineHeight: '1.4',
                                        color: isDarkMode ? '#ffffff' : '#000000',
                                        boxShadow: isDarkMode ? '0 4px 6px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    <div style={{
                                        borderBottom: isDarkMode ? '1px solid #4a5568' : '1px solid #eee',
                                        marginBottom: '8px',
                                        paddingBottom: '4px',
                                        fontWeight: 'bold'
                                    }}>
                                        Date: {dateLabel}
                                        <br />
                                        Total Cases: {totalCases}
                                    </div>
                                    {data.segments.map((segment, idx) => {
                                        // Determine border color based on segment type and date status
                                        let borderColor = '#2196F3' // Default blue for current
                                        if (segment.type === 'confirmed') {
                                            borderColor = segment.dateStatus === 'has_etd_only' ? '#00BCD4' : '#4CAF50'
                                        } else if (segment.type === 'date_unknown') {
                                            borderColor = '#FF5722'
                                        }
                                        
                                        return (
                                            <div 
                                                key={idx} 
                                                style={{
                                                    marginBottom: '8px',
                                                    paddingLeft: '8px',
                                                    borderLeft: `3px solid ${borderColor}`
                                                }}
                                            >
                                                {formatTooltipSegment(segment)}
                                            </div>
                                        )
                                    })}
                                </div>
                            )
                        }}
                    />
                </div>
            </Card.Body>
        </Card>
    )
}

export default InventoryTimelineGraph 